import { Box, Grid, Paper, Typography, useEventCallback } from '@mui/material';
import { Form, Formik } from 'formik';
import { PmiButton } from 'shell/components/shared/button';
import * as yup from 'yup';
import PmiTextField from 'shell/components/shared/text-field';
import PmiDivider from 'shell/components/shared/divider';
import { PecState, PecStateEnum } from 'shell/utils/utils-management-pec';
import StateElaborationPecList from '../../pec-filters/state-elaboration-pec-list';
import FormPecDetail from './form-detail';
import FormPecEdit from './form-edit';
import { TypeFile } from 'practice/components/detail/form/accordion-attachments/form';
import { initialValuesPecForm } from 'shell/constants/initial-values-pec-form';
import { regexEmail } from 'shell/constants/regex';
import { Pec } from 'shell/models/pec';
import { Fragment, memo } from 'react';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { toast } from 'react-toastify';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponse } from 'shell/models/http-response';
import { AxiosResponse } from 'axios';

export interface PecForm {
  data?: Pec;
  oldCode?: number;
  code?: number;
  sender?: string;
  receiver?: string;
  cc?: string;
  subject?: string;
  attachments?: Array<TypeFile>;
  signedBy?: string;
  messageDate?: string;
  messageBody?: string;
  messageNumber?: number;
  messageId?: string;
  sendDate?: string;
  transmissionStatus?: PecStateEnum;
  trasmissionStyleObject?: PecState;
  processingStatus?: PecStateEnum;
  competentUnit?: string;
  assignedTo?: string;
  descRecipient?: string;
}

const validationSchema = yup.object({
  code: yup.string().required('Il campo codice è obbligatorio'),
});

const validationSchemaEdit = yup.object({
  receiver: yup
    .string()
    .required('Il campo codice è obbligatorio')
    .matches(regexEmail, 'Formato email'),
  subject: yup.string().required('Il campo codice è obbligatorio'),
  messageBody: yup.string().required('Il campo codice è obbligatorio'),
});

type FormPecPropsDetail = {
  onOk: (form?: PecForm) => void;
  onCancel: (form?: PecForm) => void;
  title: string;
  idDetail?: boolean;
  isEdit?: never;
  initialValues?: PecForm;
};

type FormPecPropsEdit = {
  onOk: (form?: PecForm) => void;
  onCancel: (form?: PecForm) => void;
  title: string;
  idDetail?: never;
  isEdit?: boolean;
  initialValues?: PecForm;
};

type FormPecProps = (FormPecPropsDetail | FormPecPropsEdit) & { getPecDetail?: () => unknown };

const FormPecComponent = ({
  onOk,
  onCancel,
  title,
  isEdit = false,
  initialValues,
  idDetail = false,
  getPecDetail,
}: FormPecProps): React.ReactElement => {
  const onSubmit = useEventCallback((formValues: PecForm) => {
    onOk(formValues);
  });

  const onReceived = useHttpClient(``, {
    options: {
      method: 'GET',
      loading: true,
      errorCatching: true,
    },
  });

  const onChangeState = useHttpClient(`${__ACT_API_URL__}/pec/aggiornamento-stato`, {
    options: {
      method: 'PUT',
      loading: true,
      errorCatching: true,
    },
  });

  const onClickChangeState = useEventCallback((stato: string, idComunicazione?: number) => {
    (
      onChangeState({
        data: {
          codiceStato: stato,
          key: { idComunicazione },
        },
      }) as unknown as ResponsePromise<
        HttpResponse<{ contenutoTxt?: string }>,
        HttpResponse<{ contenutoTxt?: string }>
      >
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      if (getPecDetail) {
        getPecDetail();
      }
    });
  });

  const onClickReceived = useEventCallback((tipoPec: string, idComunicazione?: number) => {
    (
      onReceived({
        url: `${__ACT_API_URL__}/pec/${idComunicazione || ''}/${tipoPec}`,
      }) as unknown as ResponsePromise<
        HttpResponse<{ contenutoTxt?: string }>,
        HttpResponse<{ contenutoTxt?: string }>
      >
    ).then((res) => {
      const contenutoTxt = (res as AxiosResponse<{ contenutoTxt?: string }>)?.data?.contenutoTxt;
      const nuovaFinestra = window.open('', '_blank'); // Apre una nuova scheda vuota
      if (nuovaFinestra && contenutoTxt) {
        nuovaFinestra.document.write(contenutoTxt);
        nuovaFinestra.document.title = 'Ricevuta'; // Imposta il titolo della scheda
      }

      toast.success('Operazione eseguita con successo');
    });
  });

  return (
    <Formik
      initialValues={initialValues || initialValuesPecForm}
      validationSchema={isEdit ? validationSchemaEdit : validationSchema}
      validateOnMount
      onSubmit={onSubmit}
      enableReinitialize
      isInitialValid
      validateOnChange
      validateOnBlur
    >
      {({ values, errors, touched, setFieldValue }) => (
        <Form>
          <Grid container spacing={3} alignItems="stretch">
            <Grid item xs={12} md={isEdit ? 12 : 8} order={{ xs: 2, md: 1 }}>
              <Paper
                elevation={3}
                sx={{
                  padding: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  justifyContent: 'space-between',
                }}
              >
                <Typography gutterBottom variant="h5" component="h2">
                  {title}
                </Typography>
                {isEdit ? (
                  <FormPecEdit
                    values={values}
                    errors={errors}
                    touched={touched}
                    onChange={setFieldValue}
                  />
                ) : (
                  <FormPecDetail values={values} />
                )}
                {values.signedBy ? (
                  <Typography variant="body1">
                    <strong>Firmato da:</strong> {values.signedBy}
                  </Typography>
                ) : undefined}
                {isEdit ? undefined : (
                  <Box sx={{ backgroundColor: '#FFE6E6', padding: 2, marginTop: 2 }}>
                    <Typography variant="body1" gutterBottom>
                      Il giorno {values.messageDate} il messaggio ‘{values.subject}’ è stato inviato
                      da: {values.sender}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {values.messageBody}
                    </Typography>
                  </Box>
                )}
                <span>
                  <PmiButton
                    variant="outlined"
                    color="cdmBrand"
                    sx={{ marginTop: 2, width: 'fit-content', marginRight: '2rem' }}
                    onClick={() => onCancel()}
                  >
                    Torna indietro
                  </PmiButton>
                  {idDetail ? (
                    <Fragment>
                      <PmiButton
                        color="cdmBrandThird"
                        variant="outlined"
                        onClick={() => onClickReceived(PecStateEnum.Suspended, values.code)}
                        sx={{ marginTop: 2, width: 'fit-content', marginRight: '2rem' }}
                        disabled={
                          values.transmissionStatus?.toString() !== PecStateEnum.Accepted &&
                          values.transmissionStatus?.toString() !== PecStateEnum.FailureDeliver &&
                          values.transmissionStatus?.toString() !== PecStateEnum.Delivered
                        }
                      >
                        Ricevuta accettazione
                      </PmiButton>
                      <PmiButton
                        color="cdmBrandThird"
                        variant="outlined"
                        onClick={() => onClickReceived(PecStateEnum.Sent, values.code)}
                        sx={{ marginTop: 2, width: 'fit-content', marginRight: '2rem' }}
                        disabled={
                          values.transmissionStatus?.toString() !== PecStateEnum.FailureDeliver
                        }
                      >
                        Ricevuta mancata consegna
                      </PmiButton>
                      <PmiButton
                        color="cdmBrandThird"
                        variant="outlined"
                        onClick={() => onClickReceived(PecStateEnum.Error, values.code)}
                        sx={{ marginTop: 2, width: 'fit-content', marginRight: '2rem' }}
                        disabled={values.transmissionStatus?.toString() !== PecStateEnum.Delivered}
                      >
                        Ricevuta avvenuta consegna
                      </PmiButton>
                      <PmiButton
                        color="cdmBrandThird"
                        variant="outlined"
                        onClick={() => onClickChangeState(PecStateEnum.Suspended, values.code)}
                        sx={{ marginTop: 2, width: 'fit-content', marginRight: '2rem' }}
                        disabled={values.transmissionStatus?.toString() !== PecStateEnum.Sentable}
                      >
                        Sospendi Pec
                      </PmiButton>
                      <PmiButton
                        color="cdmBrandThird"
                        variant="outlined"
                        onClick={() => onClickChangeState(PecStateEnum.Sentable, values.code)}
                        sx={{ marginTop: 2, width: 'fit-content', marginRight: '2rem' }}
                        disabled={values.transmissionStatus?.toString() !== PecStateEnum.Suspended}
                      >
                        Invia PEC
                      </PmiButton>
                      <PmiButton
                        color="cdmBrandThird"
                        variant="outlined"
                        onClick={() => onClickChangeState(PecStateEnum.Canceled, values.code)}
                        sx={{ marginTop: 2, width: 'fit-content', marginRight: '2rem' }}
                        disabled={
                          values.transmissionStatus?.toString() === PecStateEnum.Accepted ||
                          values.transmissionStatus?.toString() === PecStateEnum.Delivered ||
                          values.transmissionStatus?.toString() === PecStateEnum.Canceled
                        }
                      >
                        Annulla PEC
                      </PmiButton>
                    </Fragment>
                  ) : undefined}
                  {isEdit ? (
                    <PmiButton
                      color="cdmBrandThird"
                      variant="outlined"
                      type="submit"
                      sx={{ marginTop: 2, width: 'fit-content' }}
                    >
                      Invia di nuovo la PEC
                    </PmiButton>
                  ) : undefined}
                </span>
              </Paper>
            </Grid>

            {idDetail ? (
              <Grid item xs={12} md={4} order={{ xs: 1, md: 2 }}>
                <Paper elevation={3} sx={{ padding: 2, marginBottom: 2 }}>
                  <Typography variant="h6">Estremi</Typography>
                  <PmiDivider sx={{ marginY: 1 }} />
                  <PmiTextField
                    label="N°"
                    value={values.messageNumber}
                    fullWidth
                    margin="normal"
                    disabled
                  />
                  <PmiTextField
                    label="Id Messaggio"
                    value={values.messageId}
                    fullWidth
                    margin="normal"
                    disabled
                  />
                  <PmiTextField
                    label="Data invio"
                    value={values.sendDate}
                    fullWidth
                    margin="normal"
                    disabled
                  />
                  <StateElaborationPecList
                    label="Stato trasmissione"
                    value={values.transmissionStatus}
                    disabled
                    onChange={(value) => setFieldValue('transmissionStatus', value)}
                  />
                </Paper>

                <Paper elevation={3} sx={{ padding: 2 }}>
                  <Typography variant="h6">Lavorazione</Typography>
                  <PmiDivider sx={{ marginY: 1 }} />
                  <Box sx={{ marginTop: 2 }}>
                    <StateElaborationPecList
                      label="Stato lavorazione"
                      value={values.processingStatus}
                      disabled
                      onChange={(value) => setFieldValue('processingStatus', value)}
                    />
                  </Box>
                  <PmiTextField
                    label="U.O competente"
                    value={values.competentUnit}
                    fullWidth
                    margin="normal"
                    disabled
                  />
                  <PmiTextField
                    label="In carico a"
                    value={values.assignedTo}
                    fullWidth
                    margin="normal"
                    disabled
                  />
                </Paper>
              </Grid>
            ) : undefined}
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

FormPecComponent.displayName = 'Form Pec Anpr component';

const FormPec = memo(FormPecComponent);
export default FormPec;
